<script setup lang="ts">
import { ref, onMounted } from "vue";
import { Permission } from "../../../types/auth";
import { Item, Variant, VariantList } from "../../../types/models";
import { api } from "../../../api/Api";
import ModalFormTemplate from "./ModalFormTemplate.vue";
import AddListItemForm from "../../../features/inventory/itemmanagement/variants/AddListItemForm.vue";
import SearchSelect from "../form/SearchSelect.vue";
import DataTable from "../../table/DataTable.vue";
import GridInput from "../form/GridInput.vue";
import Buttons from "../form/Buttons.vue";
import DropDownButton from "../common/DropdownButton.vue";
import MessageBox from "../../../services/MessageBox";

const props = defineProps<{
  data?: any | null;
  item: Item;
  permissions?: Permission[];
}>();

const emit = defineEmits<{
  (e: "select", data: any): void;
  (e: "close"): void;
}>();


const title = "Item Variants";
const endPoint = "/clients/inventory/item-management/items/";

const variantList = ref<VariantList[]>([]);
const remainigList = ref<VariantList[]>([]);

const addNewVariant = () => {
  selectedVariant.value = {
    id: selectedItem.value?.variants ?? '',
    name: String(selectedItem.value?.variant_names ?? ''),
    list: [],
    items_id: selectedItem.value?.id,
    variants_id: selectedItem.value?.variants ?? '',
    variant_lists_id: '',
    code: '',
    on_hand: 0,
    quantity: 0,
    shelfs: [],
    uid: '',
    active: true,
  }
  showAddListForm.value = true;
}

const handleSelect = async (data: any) => {
  emit("select", data);
};


const handleListAdded = async (data: any) => {
  if (data) {
    handleSelectItemVariant(data);
  }
};

const handleAssignVariant = async (data: any) => {
  if (data) {
    const confirmed = await MessageBox.confirm({
      title: 'Assign Variant',
      message: 'Are you sure you want to assign <strong>'+ data.name +'</strong> to this item?',
      confirmText: "Yes",
      cancelText: 'Cancel',
      type: 'info'
    })
    if (confirmed) {
      console.log((endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + 'variant-create')
      const response = await api.post(
        (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + 'variant-create',
        { 
          items_id: items_id.value,
          variants_id: data.variants_id,
          variant_lists_id: data.id,
          code: data.code,
          name: data.name
         }
      );
      if (response.success == true) {
        handleSelectItem({
          id: items_id.value,
        })
      }
    }
    
  }
}


const selectedItem = ref<Item>();
const handleSelectItem = async (data: any) => {
  if (data && !data.has_variants && data.variant_name) {
    MessageBox.show("This item is not configured to have variants", "warning");
    return
  }
  if (data) {
    selectedItem.value = data;
    const response = await api.post(
      (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + 'variant-get',
      data
    );
    if (response.success == true) {
      variantList.value = response.data;
      remainigList.value = response.lists;
    }
  }
};

const handleSelectItemVariant = (data: any) => {
  if (data && selectedItem.value) {
    console.log('data', data, 'selectedItem.value', selectedItem.value)
    data.code = selectedItem.value.code + data.code;
    emit('select', data);
    emit('close')
  }
}

onMounted(() => {
  if (props.data) {
    variantList.value = props.data
  }
})

const items_id = ref('');
const showAddListForm = ref(false);
const selectedVariant = ref<Variant>();
</script>
<template>
  <ModalFormTemplate
    :data="data"
    mode="view"
    width="50rem"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :tabList="[]"
    @save="handleSelect"
    :ignore-permission="true"
    @close="$emit('close')"
  >
    <template #form>
      <div>
        <div class="grid md:grid-cols-5 gap-6">
          <SearchSelect
          :end-point="endPoint"
          class="max-w-[400px] col-span-2"
          v-model="items_id"
          :label="'Select Variant'"
          @select="handleSelectItem"
          :placeholder="'Select Variant'" />
          <DropDownButton text="Assign Variant" icon="ChartColumnStacked" v-if="items_id && remainigList && remainigList.length > 0" class="mt-[25px] col-span-2">
            <Buttons v-for="variant in remainigList" :caption="variant.name" class-name="w-full px-2 py-3" :key="variant.id" @click="handleAssignVariant(variant)" />
          </DropDownButton>
          <div class="mt-[30px]">
            <Buttons caption="Add Variant" icon="Plus" @click="addNewVariant" v-if="items_id" />
          </div>
        </div>
        <GridInput label="Assigned Variant List" class="mt-4">
          <DataTable 
          :rows="variantList"
          title="Assigned Variant List"
          :fields="[
            { name: 'photo', caption: 'Photo', sortable: false, fieldType: 'image' },
            { name: 'code', caption: 'Code', sortable: false, fieldType: 'text' },
            { name: 'name', caption: 'Name', sortable: false, fieldType: 'text' },
            { name: 'active', caption: 'Active', sortable: false, fieldType: 'status' },
          ]"
          :actions="[
            { name: 'select', permission: 'view', icon: 'MousePointer2', variant: 'primary', title: 'Select', visible: () => true, ignorePermission: true, hideMobile: true,},
          ]"
          @action="(action, data) => {
            if (action === 'select') {
              handleSelectItemVariant(data);
            }
          }"
          :filterFields="[]"
          :show-header="false"
          :show-title="false"
          :show-quick-tour="false"
          @select="handleSelectItem" />
        </GridInput>
      </div>
    </template>
  </ModalFormTemplate>
  <AddListItemForm
    title="Create new Variant"
    :variant="selectedVariant"
    list-create-name="-"
    :permissions="permissions"
    :endPoint="endPoint"
    mode="create"
    @close="showAddListForm = false"
    @save="handleListAdded"
    v-if="showAddListForm"
  />
</template>